import React, { useEffect, useRef, useState } from 'react';
import {
  AptunoButton,
  BoxInformative,
  Icon,
  SIZES,
  useResponsive,
  VARIANTS,
} from '@aptuno/aptuno-ui';
import {
  AuxiliarySide,
  Border,
  BoxButton,
  BoxCallTenant,
  BoxImage,
  BoxImageMain,
  BoxStars,
  BoxVideo,
  ButtonLandlord,
  CirclePhoto,
  CirclePlayVideo,
  CloseModalButton,
  CommentPerson,
  Container,
  ContainerArticle,
  ContainerCalculator,
  ContainerComment,
  ContainerIsTenant,
  ContentArticle,
  Description,
  GoToMore,
  Item,
  ItemInformative,
  KeyBoxItem,
  KeyBoxItemText,
  LabelInformative,
  LabelName,
  LabelReadMore,
  LabelSection,
  LabelShowMore,
  LinkArticle,
  MainTitle,
  Message,
  ModalVideo,
  NamePerson,
  Play,
  SectionArticles,
  SectionCalculator,
  SectionComments,
  SectionIsTenant,
  SectionKeyBox,
  SectionPrimary,
  SectionSecondary,
  ShowMore,
  TDataSectionSecondary,
  Title,
  VoiceJournalism,
  VoiceLandlord,
} from '@components/LandingPages/LandingLandlord/LandingLandlord.styled';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectCoverflow, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import {
  DataArticlesJournalism,
  DataCommentsLandlord,
  DataSectionSecondary,
  getImageSrc,
} from './data-content';
import BubbleDecoration from '@components/LandingPages/LandingLandlord/BubbleDecoration/BubbleDecoration';
import dynamic from 'next/dynamic';
import { isColombia } from '@shared/utils/region-utils';
import { useRouter } from 'next/router';
import { useLoggedInUser } from '@shared/Authentication';
import { Head } from '@shared/Seo/components/Head';
import { useTranslations } from 'next-intl';
import PublicationPlans from './PublicationPlans/PublicationPlans';
import { useAnalyticsTracker } from '@services/analytics';
import { PublishCTAClicked } from '@contexts/Estate/Publication/events/publish-cta-clicked.event';
import { TenantCTAClicked } from '@contexts/Estate/Publication/events/tenant-cta-clicked.event';
import { CalculatorCTAClicked } from '@contexts/Estate/Publication/events/calculator-cta-clicked.event';

const LandingLandlord = () => {
  const router = useRouter();

  const { isMobile, isDesktop, isMediumSize } = useResponsive();
  const VideoLazyFrame = dynamic(
    () => import('./VideoLazyFrame/VideoLazyFrame')
  );
  const [isModalOpen, setModalOpen] = useState<string>();
  const myRef = useRef<any>();
  const loggedInUser = useLoggedInUser();
  const eventTracker = useAnalyticsTracker();
  const seoT = useTranslations('Seo');
  const [canonicalUrl, setCanonicalUrl] = useState('');
  const [isTenantButtonOpen, setTenantButtonOpen] = useState<boolean>(true);

  useEffect(() => {
    const aux = window?.location?.pathname;
    setCanonicalUrl(aux);
  }, []);

  const CTASegmentLandlord = async (labelButton: string) => {
    eventTracker.trackEvent(new PublishCTAClicked(labelButton));

    const queryParams = new URLSearchParams({
      ...router.query,
      source: 'landing-propietarios',
      create: 'true',
    });

    return router.push(`/listing-publications?${queryParams.toString()}`);
  };

  const CTASegmentTenant = (labelButton: string) => {
    eventTracker.trackEvent(new TenantCTAClicked(labelButton));
    window.location.href = '/inmuebles-arriendo';
  };

  const goToCalculatorCTA = () => {
    loggedInUser?.data?.id &&
      eventTracker.trackEvent(new CalculatorCTAClicked(loggedInUser.data.id));
    window.location.href = '/calculadora-de-arrendamiento';
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onRentAdvanceClick = () => {
    router.push('/rent-advance');
  };

  const getBoxImage = (data: TDataSectionSecondary) => {
    const imageSrc = getImageSrc(data);
    return !!imageSrc ? (
      <Image
        src={imageSrc}
        width={isMobile ? data.widthImageMobile : data.widthImageDesktop}
        height={isMobile ? data.heightImageMobile : data.heightImageDesktop}
        alt={data.altImage}
        title={'Publicar Inmueble | Aptuno'}
      />
    ) : undefined;
  };

  return (
    <Container
      isMobile={isMobile}
      isDesktop={isDesktop}
      isMediumSize={isMediumSize}
    >
      <Head
        title={seoT('pageview.LandingLandlord.title')}
        description={seoT('pageview.LandingLandlord.description')}
        image={seoT('homeSeoImage')}
        url={canonicalUrl}
      />
      <ContainerIsTenant isOpenTenantButton={isTenantButtonOpen}>
        <SectionIsTenant>
          <LabelInformative color={'#000'}>
            Si estás buscando tu próximo hogar,{' '}
            <a
              onClick={() =>
                CTASegmentTenant('Si estás buscando tu próximo hogar')
              }
            >
              haz click aquí
            </a>
          </LabelInformative>
          <Icon
            icon="closeAlt"
            color={'#000'}
            onClick={() => setTenantButtonOpen(() => !isTenantButtonOpen)}
            width={'10px'}
            height={'10px'}
          ></Icon>
        </SectionIsTenant>
      </ContainerIsTenant>
      <BubbleDecoration />
      {isDesktop && <AuxiliarySide className="right_aux_grid_1" />}
      {isDesktop && <AuxiliarySide className="left_aux_grid_1" />}
      <SectionPrimary
        isDesktop={isDesktop}
        isMediumSize={isMediumSize}
        className="section_content"
      >
        <MainTitle
          isDesktop={isDesktop}
          isMediumSize={isMediumSize}
          className="grid_1"
        >
          Publica tu inmueble, encuentra inquilino y recibe tus pagos.
        </MainTitle>

        <BoxImageMain
          isDesktop={isDesktop}
          isMediumSize={isMediumSize}
          className="grid_3"
        >
          <Image
            src={`/images/landing-pages/landing-landlord/${
              isColombia() ? 'landlord01.svg' : 'landlord01mx.svg'
            }`}
            width={isMobile ? 279 : 389}
            height={isMobile ? 188 : 301}
            alt="publicar inmueble, casa o apartamento para arrendar con Aptuno"
            title="Publicar Inmueble en Aptuno"
          />
        </BoxImageMain>

        <BoxButton
          isDesktop={isDesktop}
          isMediumSize={isMediumSize}
          className="grid_1"
        >
          <Message isMediumSize={isMediumSize}>¡Así de fácil!</Message>
          <Item isMediumSize={isMediumSize}>
            <AptunoButton
              onClick={() => CTASegmentLandlord('Empezar mi publicación')}
              appearance={VARIANTS.PRIMARY}
              size={SIZES.MEDIUM}
            >
              Empezar mi publicación
              <Icon icon="arrowrightalt" />
            </AptunoButton>
          </Item>
        </BoxButton>
      </SectionPrimary>
      <BoxCallTenant
        isDesktop={isDesktop}
        isMediumSize={isMediumSize}
        className="section_content"
      >
        <ItemInformative>
          <BoxInformative refIcon="info" appearance="informative">
            <LabelInformative color={'#6c757d'}>
              Si estás buscando tu hogar ideal, {isMobile && <br />}
              <a
                onClick={() =>
                  CTASegmentTenant(
                    'Si estás buscando tu hogar ideal, haz click aquí'
                  )
                }
              >
                haz click aquí
              </a>
            </LabelInformative>
          </BoxInformative>
        </ItemInformative>
        {isDesktop && (
          <ShowMore
            onClick={() =>
              window.scrollTo({
                behavior: 'smooth',
                top: myRef.current.offsetTop,
              })
            }
          >
            <LabelShowMore>Conoce más</LabelShowMore>
            <Image
              src={
                '/images/landing-pages/landing-landlord/icons/icon_show_more.svg'
              }
              width="110px"
              height="110px"
              alt="beneficios de Aptuno para arrendar inmuebles"
              title="Beneficios de Aptuno para Arrendar Inmuebles"
            />
          </ShowMore>
        )}
      </BoxCallTenant>
      <GoToMore ref={myRef} />
      {DataSectionSecondary.map((el, index) => {
        return (
          <SectionSecondary
            key={index}
            isDesktop={isDesktop}
            isMediumSize={isMediumSize}
            className="section_content"
          >
            <Title isDesktop={isDesktop} className="grid_1">
              {el.title}
            </Title>
            <Description isDesktop={isDesktop} className="grid_2">
              {el.description}
            </Description>

            <ButtonLandlord
              onClick={() =>
                el.id === '4'
                  ? onRentAdvanceClick()
                  : CTASegmentLandlord(el?.messageButton)
              }
              borderColor={el.borderColor}
              iconColor={el.iconColor}
              isDesktop={isDesktop}
              className="grid_3"
            >
              {el.messageButton}
              <Icon icon="arrowrightalt" />
            </ButtonLandlord>

            <BoxImage isMediumSize={isMediumSize} className="grid_4">
              {getBoxImage(el)}
            </BoxImage>
          </SectionSecondary>
        );
      })}
      <SectionKeyBox isDesktop={isDesktop} className="section_content">
        <Title isDesktop={isDesktop} className="grid_1">
          Despreocúpate por atender las visitas
        </Title>
        <Description isDesktop={isDesktop} className="grid_2">
          Entréganos una copia de tus llaves e instalaremos una Cajilla de
          Seguridad en tu inmueble. Siempre podremos mostrarlo a los inquilinos
          que quieran visitar, sin molestarte.
        </Description>
        <div className="grid_3">
          <KeyBoxItem isDesktop={isDesktop}>
            <Icon width="14px" icon="check" aria-label="check" />
            <KeyBoxItemText>
              No tendrás que salir de tu casa u oficina para mostrar tu
              inmueble.
            </KeyBoxItemText>
          </KeyBoxItem>
          <KeyBoxItem isDesktop={isDesktop}>
            <Icon width="14px" icon="check" aria-label="check" />
            <KeyBoxItemText>
              Los propietarios que deciden instalar una cajilla, arriendan su
              inmueble en un promedio de 17 días.
            </KeyBoxItemText>
          </KeyBoxItem>
          <KeyBoxItem isDesktop={isDesktop}>
            <Icon width="14px" icon="check" aria-label="check" />
            <KeyBoxItemText>
              Un inmueble con cajilla de seguridad recibe hasta 2 veces más
              visitas.
            </KeyBoxItemText>
          </KeyBoxItem>
        </div>
        <BoxImage isMediumSize={isMediumSize} className="grid_4">
          <Image
            src="/images/landing-pages/landing-landlord/arte_cajilla.svg"
            width={isMobile ? 205 : 288}
            height={isMobile ? 156 : 293}
            alt="Solicitar cajilla"
            title={'Cajilla | Aptuno'}
          />
        </BoxImage>
      </SectionKeyBox>
      {isDesktop && (
        <AuxiliarySide
          className="left_aux_grid_7"
          backgroundColor="#EBFAF1"
          height="550px"
        />
      )}
      {isDesktop && (
        <AuxiliarySide
          className="right_aux_grid_7"
          backgroundColor="#EBFAF1"
          height="550px"
        />
      )}
      <SectionCalculator isDesktop={isDesktop} className="section_content">
        <LabelSection isDesktop={isDesktop} isMediumSize={isMediumSize}>
          ¿No sabes cuánto cobrar por tu arriendo?
        </LabelSection>
        <ContainerCalculator>
          <ButtonLandlord
            onClick={() => goToCalculatorCTA()}
            borderColor="#B1EDC9"
            iconColor="#B1EDC9"
            background="#EBFAF1"
            isDesktop={isDesktop}
            className="grid_3"
          >
            Descúbrelo aquí
            <Icon icon="arrowrightalt" />
          </ButtonLandlord>
          <BoxImage isMediumSize={isMediumSize} className="grid_4">
            <Image
              src="/images/landing-pages/landing-landlord/hombre-megafono.svg"
              width={isMobile ? 205 : 288}
              height={isMobile ? 156 : 293}
              alt="calcular valor de arriendo"
              title={'Calcular canon | Aptuno'}
            />
          </BoxImage>
        </ContainerCalculator>
      </SectionCalculator>
      <PublicationPlans />
      {isDesktop && isColombia() && (
        <AuxiliarySide
          className="left_aux_grid_9"
          backgroundColor="#FFF9EB"
          height="673px"
        />
      )}
      {isDesktop && isColombia() && (
        <AuxiliarySide
          className="right_aux_grid_9"
          backgroundColor="#FFF9EB"
          height="673px"
        />
      )}
      {isColombia() && (
        <SectionComments isDesktop={isDesktop} className="section_content">
          <LabelSection isDesktop={isDesktop} isMediumSize={isMediumSize}>
            Lo que dicen nuestros propietarios:
          </LabelSection>

          <ModalVideo
            isDesktop={isDesktop}
            id="modal_id_youtube"
            isOpenModal={isModalOpen}
            onClick={(el) =>
              setModalOpen(
                el.target['id'] == 'modal_id_youtube' ||
                  el.target['id'] == 'close_id_modal'
                  ? 'none'
                  : 'block'
              )
            }
          >
            <CloseModalButton isDesktop={isDesktop} id="close_id_modal">
              &times;
            </CloseModalButton>
            <VideoLazyFrame />
          </ModalVideo>

          <BoxVideo isDesktop={isDesktop}>
            <CirclePhoto>
              <Image
                src="/images/landing-pages/landing-landlord/photo_mabel_moreno.svg"
                width={isMobile ? 200 : 264}
                height={isMobile ? 200 : 264}
                alt="Mabel Moreno propietaria de Aptuno arrienda su inmueble"
                title="Mabel Moreno propietaria de Aptuno arrienda su inmueble"
              />
            </CirclePhoto>
            <CirclePlayVideo isDesktop={isDesktop}>
              <Play isDesktop={isDesktop} onClick={() => setModalOpen('block')}>
                <Image
                  src={
                    '/images/landing-pages/landing-landlord/icons/icon_play_video_01.svg'
                  }
                  width={isMobile ? 90 : 99}
                  height={isMobile ? 90 : 99}
                  alt="arrendar inmueble rápido"
                  title="Arrendar Inmueble Rápido con Aptuno"
                  className="play_order_1"
                />
              </Play>
              <Border isDesktop={isDesktop}>
                <Image
                  src="/images/landing-pages/landing-landlord/icons/icon_play_video_02.svg"
                  width={isMobile ? 108 : 120}
                  height={isMobile ? 104 : 120}
                  alt="publicar inmueble en Aptuno"
                  title="Publicar Inmueble en Aptuno"
                  className="play_order_2"
                />
              </Border>
            </CirclePlayVideo>
          </BoxVideo>

          <VoiceLandlord isDesktop={isDesktop}>
            <Swiper
              effect={isMobile ? 'coverflow' : 'fade'}
              grabCursor={true}
              centeredSlides={isMobile ? true : undefined}
              slidesPerView={'auto'}
              coverflowEffect={{
                rotate: 50,
                stretch: 10,
                depth: 100,
                modifier: 1,
                slideShadows: false,
              }}
              pagination={true}
              modules={[EffectCoverflow, Pagination, Autoplay]}
              className="mySwiper"
              loop={isMobile ? true : false}
              autoplay={{
                delay: 5000,
                disableOnInteraction: true,
              }}
            >
              {DataCommentsLandlord.map((el, index) => {
                return (
                  <SwiperSlide key={index}>
                    <ContainerComment>
                      <LabelName>
                        <NamePerson>{el.namePerson}</NamePerson>
                        {el.checked && (
                          <Image
                            src={
                              '/images/landing-pages/landing-landlord/icons/icon_checked.svg'
                            }
                            width={20}
                            height={20}
                            alt={el.altImage}
                            title={'Propietarios de Aptuno | Arrendar Inmueble'}
                          />
                        )}
                      </LabelName>
                      <CommentPerson>{el.comment}</CommentPerson>
                      <BoxStars>
                        {[...Array(el.star)].map((star, index) => (
                          <Image
                            key={index}
                            src="/images/landing-pages/landing-landlord/icons/icon_star.svg"
                            width={20}
                            height={20}
                            alt="publicar inmueble rápido y sencillo para arrendar"
                            title="Publicar Inmueble Rápido y Sencillo Para Arrendar | Aptuno"
                          />
                        ))}
                      </BoxStars>
                    </ContainerComment>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </VoiceLandlord>
        </SectionComments>
      )}

      <SectionArticles isDesktop={isDesktop} className="section_content">
        <LabelSection isDesktop={isDesktop} isMediumSize={isMediumSize}>
          Aptuno en la prensa:
        </LabelSection>
        <VoiceJournalism isDesktop={isDesktop}>
          <Swiper
            effect={isMobile ? 'coverflow' : 'fade'}
            grabCursor={true}
            centeredSlides={isMobile ? true : undefined}
            slidesPerView={'auto'}
            coverflowEffect={{
              rotate: 50,
              stretch: 10,
              depth: 100,
              modifier: 1,
              slideShadows: false,
            }}
            pagination={true}
            modules={[EffectCoverflow, Pagination, Autoplay]}
            className="mySwiper"
            loop={isMobile ? true : false}
            autoplay={{
              delay: 5000,
              disableOnInteraction: true,
            }}
          >
            {DataArticlesJournalism.map((el, index) => {
              return (
                <SwiperSlide key={index}>
                  <ContainerArticle>
                    <Image
                      src={el.logoJournalism}
                      width={80}
                      height={36}
                      alt={el.articleContent}
                      title="Articulos de Aptuno"
                    />
                    <ContentArticle>{el.articleContent}</ContentArticle>
                    <LinkArticle
                      target="_blank"
                      href={el.articleUrl}
                      rel="nofollow"
                    >
                      <LabelReadMore>
                        Leer artículo
                        <Image
                          src={
                            '/images/landing-pages/landing-landlord/icons/icon_arrow_diagonal.svg'
                          }
                          width={12}
                          height={12}
                          alt={'noticias de Aptuno'}
                          title="Noticias de Aptuno"
                        />
                      </LabelReadMore>
                    </LinkArticle>
                  </ContainerArticle>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </VoiceJournalism>
      </SectionArticles>
    </Container>
  );
};

export default LandingLandlord;
